/**
 * Deutscher Katalog der Personal-App.
 *
 * Typisiert gegen `es.ts`: fehlt hier ein Schlüssel oder ist er falsch
 * geschrieben, kompiliert TypeScript nicht. Genau dafür sind die Kataloge
 * flach — eine Übersetzung, die still auf einen leeren Text zurückfällt, ist
 * schlimmer als eine, die gar nicht erst ausgeliefert wird.
 *
 * ### Aquí se tutea, y en la página del huésped no
 *
 * Quien usa esta app trabaja en la casa y la abre cada mañana: el «du» es lo
 * natural, igual que el «tú» del español. El huésped es un desconocido de una
 * agencia y va de usted (`huesped-de.ts`). No es un descuido que los dos
 * catálogos no concuerden: es la misma distinción que el español hace con el
 * tono y el alemán hace con la gramática.
 *
 * ### Y el alemán es el idioma LARGO de los tres
 *
 * Esta app se usa en un móvil de 390 px. «Upload-Warteschlange» son 20
 * caracteres donde el español pone «Cola de subida» y el inglés «Upload
 * queue»: cualquier caja dimensionada mirando el español se queda corta aquí.
 * Por eso los textos van cortos a propósito y por eso `desbordes.mjs` recorre
 * también el alemán. Ya pasó con el inglés en el pie de la barra lateral del
 * panel, y ese solo sacaba 18 px.
 */

import type { es } from './es.js';

export const de: Record<keyof typeof es, string> = {
    // --- Gemeinsam -----------------------------------------------------
    'comun.atras': 'Zurück',
    'comun.cerrar': 'Schließen',
    'comun.reintentar': 'Erneut versuchen',
    'comun.aceptar': 'OK',
    'comun.cargando': 'Wird geladen…',
    'comun.de': 'von',
    'comun.sin_conexion': 'Offline · alles auf dem Handy gespeichert',

    // --- 00 · Gerät anmelden -------------------------------------------
    'alta.etiqueta': 'ERSTE ANMELDUNG · NUR EINMAL',
    'alta.titulo': 'Handy verknüpfen',
    'alta.intro':
        'Dieser Link funktioniert nur einmal. Nach dem Verknüpfen hat dein Handy eigene Zugangsdaten und du brauchst den Link nicht mehr.',
    'alta.paso_invitacion': 'Einladung geprüft',
    'alta.paso_invitacion_sub': 'Gültiger Link für {empleada}',
    'alta.paso_pin': 'Wähle deine 6-stellige PIN',
    'alta.paso_pin_sub': 'Damit startest du jeden Tag deine Schicht',
    'alta.paso_instalar': 'App zum Startbildschirm hinzufügen',
    'alta.paso_instalar_ios':
        'Teilen → „Zum Home-Bildschirm“. Danach öffnest du das Symbol und gibst nur deine PIN ein.',
    'alta.paso_instalar_android':
        'Menü → „App installieren“. Danach öffnest du das Symbol und gibst nur deine PIN ein.',
    'alta.paso_instalar_otro':
        'Danach kommst du über diese Adresse mit deiner PIN zurück. Der Einladungslink wird nicht mehr gebraucht.',
    'alta.dispositivo': 'Dieses Gerät',
    'alta.dev_movil': 'Handy',
    'alta.dev_video': 'Video',
    'alta.dev_sin_video': 'nicht unterstützt',
    'alta.dev_segundo_plano': 'Upload im Hintergrund',
    'alta.dev_si': 'ja',
    'alta.dev_no': 'nein',
    'alta.nota_revocacion':
        'Das Büro kann dieses Handy jederzeit sperren. Wenn du es verlierst, sag noch am selben Tag Bescheid.',
    'alta.boton': 'Verknüpfen und PIN anlegen',
    'alta.sin_red': 'Keine Verbindung',
    'alta.sin_red_ayuda':
        'Für die Anmeldung brauchst du Internet: dieser Link wird auf dem Server geprüft. Geh ins WLAN oder ins mobile Netz und versuch es erneut. Danach läuft die App auch ohne Empfang.',
    'alta.reintentar': 'Erneut versuchen',
    'alta.error_pin_existente':
        'Du hast schon eine PIN. Für dieses Handy gib die PIN ein, die du benutzt — keine neue.',
    'alta.error_bloqueada': 'Dieser Link ist gesperrt',
    'alta.error_bloqueada_ayuda':
        'Zu viele falsche PINs. Bitte das Büro um einen neuen Link; wenn du deine PIN nicht mehr weißt, lass sie dort zurücksetzen.',
    'alta.sin_token': 'Dieser Link ist ungültig',
    'alta.sin_token_ayuda':
        'Öffne den vollständigen Link vom Büro, mit Code. Wenn du ihn abgetippt hast, ist er vielleicht abgeschnitten.',
    'alta.error_caducada': 'Die Einladung ist abgelaufen',
    'alta.error_caducada_ayuda': 'Einladungen gelten 24 Stunden. Bitte das Büro um eine neue.',
    'alta.error_usada': 'Diese Einladung wurde schon benutzt',
    'alta.error_usada_ayuda':
        'Wenn du das Handy gewechselt hast, muss das Büro eine neue Einladung ausstellen.',
    'alta.paso_invitacion_sub_abierta': 'Gültiger, unbenutzter Link',
    'alta.paso_datos': 'Name und Daten eintragen',
    'alta.paso_datos_sub': 'Nur der Name ist Pflicht',
    'alta.datos_titulo': 'Deine Daten',
    'alta.datos_intro':
        'Diesen Namen sieht das Büro, und er steht auf jedem Video, das du aufnimmst. Schreib ihn so, wie du genannt werden willst.',
    'alta.campo_nombre': 'Dein Name',
    'alta.campo_nombre_ph': 'z. B. Marta G.',
    'alta.campo_telefono': 'Telefon',
    'alta.campo_email': 'E-Mail',
    'alta.campo_idioma': 'Sprache der App',
    'alta.opcional': 'optional',
    'alta.falta_nombre': 'Trag deinen Namen ein, um weiterzumachen',
    'alta.error_email': 'Diese E-Mail sieht nicht richtig aus',
    'alta.error_email_repetido': 'Diese E-Mail ist schon angemeldet',
    'alta.continuar': 'Weiter',
    'alta.hola': 'Hallo, {empleada}',

    // --- PIN anlegen und eingeben ---------------------------------------
    'pin.crear_titulo': 'Wähle deine PIN',
    'pin.crear_intro': 'Sechs Ziffern. Teile sie mit niemandem, auch nicht mit dem Büro.',
    'pin.repetir_titulo': 'PIN wiederholen',
    'pin.no_coincide': 'Die beiden PINs stimmen nicht überein',
    'pin.debil_iguales': 'Sie darf nicht sechsmal dieselbe Ziffer sein',
    'pin.saludo': 'Willkommen zurück',
    'pin.subtitulo':
        '{empleada} · {dispositivo}. Gib deine 6-stellige PIN ein, um die Schicht zu starten.',
    'pin.error': 'Falsche PIN',
    'pin.error_intentos': 'Falsche PIN · noch {intentos} Versuch{intentos:|e}',
    'pin.bloqueado': 'Zum Entsperren im Büro melden',
    'pin.bloqueado_ayuda':
        'Dieses Handy ist nach 5 Versuchen gesperrt. Nur das Büro kann es entsperren.',
    'pin.olvidado': 'Ich habe meine PIN vergessen',
    'pin.olvidado_ayuda':
        'Eine PIN lässt sich nicht wiederherstellen. Frag im Büro: dort wird sie zurückgesetzt und du wählst auf demselben Handy eine neue.',
    'pin.reiniciado_titulo': 'Wähle eine neue PIN',
    'pin.reiniciado_intro':
        'Das Büro hat deine PIN zurückgesetzt. Wähle eine neue mit sechs Ziffern: nur du kennst sie.',
    'pin.reiniciado_ya': 'Deine PIN gilt weiter. Gib die alte ein.',
    'pin.borrar': 'Löschen',

    // --- 02 · Meine Wohnungen heute -------------------------------------
    'hoy.saludo': 'Guten Morgen, {nombre}',
    'hoy.saludo_tarde': 'Guten Tag, {nombre}',
    'hoy.progreso': '{hechas} von {total} erledigt',
    'hoy.progreso_sub': 'Heute noch {pendientes} · {subiendo} beim Hochladen',
    'hoy.progreso_sub_sin_subida': 'Heute noch {pendientes}',
    'hoy.grupo_pendientes': 'OFFEN',
    'hoy.grupo_completadas': 'ERLEDIGT',
    'hoy.grupo_libres': 'Frei',
    'hoy.grupo_libres_ayuda': 'Die hat niemand zugewiesen. Nimm eine, wenn du Zeit hast.',
    'hoy.coger': 'Nehmen',
    'hoy.urgente': 'Dringend',
    'hoy.dormitorios': '{n} Schlafz.',
    'hoy.salida': 'Abreise {hora}',
    'hoy.empezar': 'Starten',
    'hoy.continuar': 'Weiter',
    'hoy.completada': '{hora} · Video {duracion} hochgeladen',
    'hoy.completada_fotos': '{hora} · {fotos} Fotos hochgeladen',
    'hoy.subiendo': 'Hochladen {pct}%',
    'hoy.vacio': 'Für heute ist dir keine Reinigung zugewiesen',
    'hoy.vacio_ayuda':
        'Wenn das nicht stimmt, sag im Büro Bescheid. Du kannst auch den QR-Code einer Wohnung scannen.',
    'hoy.escanear': 'QR scannen',
    'hoy.menu': 'Menü',
    'hoy.menu_cola': 'Warteschlange',
    'hoy.menu_idioma': 'Sprache',
    'hoy.menu_salir': 'Abmelden',

    // --- 03 · QR / NFC ---------------------------------------------------
    'qr.titulo': 'QR-Code der Wohnung scannen',
    'qr.ayuda': 'Halte die Kamera auf den Code neben der Tür. Er öffnet sich von selbst.',
    'qr.manual': 'Code von Hand eingeben',
    'qr.manual_titulo': 'Code der Wohnung',
    'qr.manual_ayuda': 'Die 32 Zeichen unter dem QR-Code.',
    'qr.camara_sistema': 'Nimm die Kamera des Handys',
    'qr.camara_sistema_ayuda':
        'Öffne die normale Kamera und halte sie auf den Code: der Link öffnet diese App in der richtigen Wohnung. In diesem Browser kannst du von hier aus nicht scannen.',
    'qr.escaneando': 'Code wird gesucht…',
    'qr.sin_permiso': 'Kein Zugriff auf die Kamera',
    'qr.no_asignado': 'Diese Wohnung steht heute nicht auf deiner Liste',
    'qr.no_asignado_ayuda':
        '{apartamento} ist heute nicht dran. Wenn du trotzdem da bist, sag im Büro Bescheid, bevor du anfängst.',

    // --- 04 · Rundgang durch die Räume ------------------------------------
    'recorrido.subtitulo_video': 'Geführter Rundgang · {n} {n:Raum|Räume}',
    'recorrido.subtitulo_fotos': 'Fotos je Raum · {n} {n:Raum|Räume}',
    'recorrido.modo_video': 'Geführtes Video',
    'recorrido.modo_video_sub': 'Kurzer Clip je Raum',
    'recorrido.modo_fotos': 'Fotos',
    'recorrido.modo_fotos_sub': 'Checkliste je Raum',
    'recorrido.siguiente_etiqueta': 'Als Nächstes',
    'recorrido.grabar': '{estancia} aufnehmen',
    'recorrido.fotografiar': 'Fotos von {estancia}',
    'recorrido.terminar': 'Zur Zusammenfassung',
    'recorrido.no_accesible': 'Als nicht zugänglich melden',
    'recorrido.no_accesible_titulo': 'Kein Zugang möglich',
    'recorrido.no_accesible_ayuda':
        'Sag uns, warum du nicht hineinkonntest. Das wird bei der Reinigung vermerkt.',
    'recorrido.no_accesible_ejemplo': 'z. B. der Gast war noch drin',
    'recorrido.sin_estancias': 'Für diese Wohnung sind keine Räume angelegt',
    'recorrido.sin_estancias_ayuda':
        'Ohne Räume lässt sich kein Nachweis der Reinigung hinterlassen. Sag im Büro Bescheid, damit sie angelegt werden; du kannst den Besuch beenden, aber ohne Aufnahme.',

    // --- 05 · Aufnahme ----------------------------------------------------
    'grabar.contexto': '{estancia} · {n}/{total}',
    'grabar.guia_titulo': 'Nimm es in dieser Reihenfolge auf',
    'grabar.empezar': 'Aufnahme starten',
    'grabar.parar': 'Stopp',
    'grabar.repetir': 'Neu aufnehmen',
    'grabar.linterna': 'Licht',
    'grabar.corto': 'Zu kurz: mindestens {segundos} Sekunden',
    'grabar.aviso_largo': 'Noch {segundos} s',
    'grabar.sin_camara': 'Kein Zugriff auf die Kamera',
    'grabar.sin_camara_ios':
        'Auf dem iPhone: Einstellungen → Safari → Kamera → Erlauben. Danach die App wieder über das Symbol öffnen.',
    'grabar.sin_camara_android':
        'Tippe auf das Schloss in der Adressleiste → Berechtigungen → Kamera → Zulassen.',
    'grabar.sin_camara_pwa':
        'Die Berechtigung gilt je Seite. Wenn du die App auf dem Startbildschirm installiert hast, gib sie hier noch einmal.',

    // --- 06 · Fotos nach Checkliste ----------------------------------------
    'fotos.titulo': '{estancia} · Fotos',
    'fotos.instruccion':
        'Mach ein Foto von jedem Punkt. Mindestens {n}, um den Raum abschließen zu können.',
    'fotos.hacer': 'Foto machen',
    'fotos.rehacer': 'Wiederholen',
    'fotos.faltan': 'Es fehl{n:t|en} noch {n} Foto{n:|s}',
    'fotos.aviso_incidencia':
        'Etwas kaputt oder verschmutzt? Melde es, dann sagen wir dem Büro Bescheid.',
    'fotos.anadir_incidencia': 'Meldung hinzufügen',
    'fotos.siguiente_estancia': 'Nächster Raum',

    // --- Meldungen ---------------------------------------------------------
    'incidencia.titulo': 'Neue Meldung',
    'incidencia.texto': 'Was hast du gesehen (Pflicht)',
    'incidencia.texto_ejemplo': 'z. B. die Duschwand hat einen Riss',
    'incidencia.gravedad': 'Dringlichkeit',
    'incidencia.gravedad_baja': 'Gering',
    'incidencia.gravedad_media': 'Normal',
    'incidencia.gravedad_alta': 'Dringend',
    'incidencia.guardar': 'Meldung speichern',
    'incidencia.guardada': 'Meldung erfasst · das Büro sieht sie',

    // --- 07 · Zusammenfassung und Abschluss --------------------------------
    'resumen.titulo': 'Reinigung erfasst',
    'resumen.subtitulo': '{apartamento} · {fecha}, {hora} · {duracion}',
    'resumen.estancias': 'Räume',
    'resumen.videos': 'Videos',
    'resumen.fotos': 'Fotos',
    'resumen.tamano': 'Größe',
    'resumen.incidencias': 'Meldungen',
    'resumen.sello': 'Siegel des Nachweises',
    'resumen.sello_gps': 'GPS {lat}, {lng} · ±{precision} m',
    'resumen.sello_sin_gps': 'Ohne GPS: das Handy hat keine Position geliefert',
    'resumen.sello_dispositivo': '{dispositivo} · App {version}',
    'resumen.nota': 'Notiz für das Büro (optional)',
    'resumen.nota_ejemplo': 'z. B. die Terrasse war voller Sand, hat länger gedauert',
    'resumen.finalizar': 'Abschließen und hochladen',
    'resumen.pendientes': 'Noch {n} {n:Raum|Räume} nicht abgeschlossen',
    'resumen.pendientes_ayuda':
        'Schließ sie ab oder melde sie mit Grund als nicht zugänglich. Mit offenen Räumen lässt sich nicht abschließen.',

    // Dieser Text ÄNDERT sich je nach Handy: auf dem iPhone lässt sich nicht
    // versprechen, dass bei geschlossener App hochgeladen wird — Safari kann
    // das nicht. Siehe D3.
    'resumen.subida_fondo': 'Wird im Hintergrund hochgeladen, auch bei geschlossener App',
    'resumen.subida_primer_plano': 'Lass die App offen, bis der Upload fertig ist',
    'resumen.subida_wifi': 'Warten auf WLAN zum Hochladen',

    // --- 08 · Warteschlange -------------------------------------------------
    'cola.titulo': 'Warteschlange',
    'cola.resumen': '{n} Reinigung{n:|en} {n:wartet|warten} · {tamano}',
    'cola.resumen_vacia': 'Alles hochgeladen. Auf dem Handy liegt nichts mehr.',
    'cola.subiendo': '{pct}% · {enviado}/{total}',
    'cola.reanudable': 'Fortsetzbar · Teile von 5 MB',
    'cola.en_cola': 'Wartet',
    'cola.fallida': '{n} Mal fehlgeschlagen',
    'cola.reintentar_ahora': 'Jetzt erneut versuchen',
    'cola.esperando_red': 'Warten auf Verbindung',
    'cola.esperando_wifi': 'Warten auf WLAN',
    'cola.solo_wifi': 'Nur über WLAN hochladen',
    'cola.almacenamiento': 'Speicher auf dem Handy {usado} / Gerätegrenze {limite}',
    'cola.almacenamiento_aviso':
        'Auf dem Handy wird der Platz knapp. Geh ins WLAN, damit die Warteschlange leer wird, bevor du weiter aufnimmst.',
    'cola.pantalla_encendida': 'Lass den Bildschirm an, solange hochgeladen wird',

    // --- Netz- und Sitzungsfehler -------------------------------------------
    'error.sin_red': 'Keine Verbindung',
    'error.sin_red_ayuda':
        'Du kannst weiterarbeiten: alles wird auf dem Handy gespeichert und hochgeladen, sobald das Netz zurück ist.',
    'error.dispositivo_revocado': 'Dieses Handy ist nicht mehr freigegeben',
    'error.inesperado': 'Etwas ist schiefgelaufen',
    'alta.rgpd_titulo': 'Datenschutz',
    'alta.rgpd_que':
        'Gespeichert werden dein Name, das Handy, das du verbindest, und — wenn du sie einträgst — deine Telefonnummer und deine E-Mail. Dein Name steht im Dateinamen jedes Videos, das du aufnimmst, und das Büro sieht ihn.',
    'alta.rgpd_plazo': 'Reinigungen werden {dias} Tag{dias:|e} lang gespeichert.',
    'alta.rgpd_plazo_indefinido': 'Reinigungen werden ohne feste Frist gespeichert.',
    'alta.rgpd_anonimiza': 'Danach wird auch gelöscht, wer sie gemacht hat.',
    'alta.rgpd_responsable': 'Verantwortlicher',
    'alta.rgpd_derechos':
        'Du kannst Auskunft, Berichtigung oder Löschung deiner Daten verlangen — schreib an {contacto}.',
    'alta.rgpd_sin_configurar':
        'Für diese Installation sind noch keine Kontaktdaten des Verantwortlichen hinterlegt. Frag im Büro danach.',
} as const;
